import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import AdminLogin from './AdminLogin';
import AdminDashboard from './AdminDashboard';

const AdminRoute = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [adminUser, setAdminUser] = useState(null);
  const [adminTier, setAdminTier] = useState(() => localStorage.getItem('adminTier') || 'admin');

  useEffect(() => {
    checkAdminAuth();
  }, []);

  const checkAdminAuth = async () => {
    try {
      // Check if admin session exists
      const adminSession = localStorage.getItem('adminSession');
      const storedUser = localStorage.getItem('adminUser');

      if (adminSession === 'true' && storedUser) {
        const user = JSON.parse(storedUser);
        
        // Verify user is still valid
        const { data: { user: currentUser }, error } = await supabase.auth.getUser();
        
        if (error || !currentUser || currentUser.id !== user.id) {
          // Session expired or invalid
          localStorage.removeItem('adminSession');
          localStorage.removeItem('adminUser');
          setIsAuthenticated(false);
        } else {
          // Check if user still has admin role
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('role')
            .eq('id', currentUser.id)
            .single();

          const isAdmin =
            profile?.role === 'admin' || profile?.role === 'super_admin';

          if (profileError || !isAdmin) {
            // User no longer has admin privileges
            localStorage.removeItem('adminSession');
            localStorage.removeItem('adminUser');
            await supabase.auth.signOut();
            setIsAuthenticated(false);
          } else {
            setIsAuthenticated(true);
            setAdminUser(currentUser);
          }
        }
      } else {
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Error checking admin auth:', error);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginSuccess = (user) => {
    setIsAuthenticated(true);
    setAdminUser(user);
    setAdminTier(localStorage.getItem('adminTier') || 'admin');
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      localStorage.removeItem('adminSession');
      localStorage.removeItem('adminUser');
      localStorage.removeItem('adminTier');
      setIsAuthenticated(false);
      setAdminUser(null);
      setAdminTier('admin');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-200 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-700 font-medium">Checking admin authentication...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AdminLogin onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <div className="AdminApp bg-slate-100 min-h-screen">
      <div className="bg-white shadow-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-slate-800">
                Admin: {adminUser?.email}
              </span>
              <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                adminTier === 'super_admin'
                  ? 'bg-indigo-100 text-indigo-800'
                  : 'bg-slate-200 text-slate-700'
              }`}>
                {adminTier === 'super_admin' ? 'super admin' : 'admin'}
              </span>
            </div>
            <button
              onClick={handleLogout}
              className="px-4 py-2 text-sm font-medium text-slate-800 bg-slate-200 rounded-md hover:bg-slate-300 border border-slate-300"
            >
              Logout
            </button>
          </div>
        </div>
      </div>
      
      <AdminDashboard />
    </div>
  );
};

export default AdminRoute; 